import { useState } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function FreeAudit() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    website: '',
    businessName: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await supabase.functions.invoke('handle-contact-form', {
        body: {
          name: formData.name,
          email: formData.email,
          website: formData.website,
          businessName: formData.businessName,
          message: `Free audit request for ${formData.website || 'website to be provided'}`,
          formType: 'audit',
        },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      setSubmitStatus('success');
      setFormData({ name: '', email: '', website: '', businessName: '' });
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="free-audit" className="relative bg-[#0a0a0a] py-20 md:py-40 overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-tl from-[#1a0033]/20 via-transparent to-[#001a33]/20 animate-gradient-reverse"></div>
      </div>

      <div className="relative z-10 max-w-[1200px] mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-20 items-center">
          {/* Left Content */}
          <div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 md:mb-8">Get Your Free Website Audit</h2>
            
            {/* Audit Type Clarification */}
            <div className="bg-gradient-to-r from-[#8b2bd9]/20 to-[#0066ff]/20 border border-[#8b2bd9]/30 rounded-xl p-4 md:p-5 mb-6 md:mb-8">
              <p className="text-white/90 text-sm md:text-base mb-2">
                <i className="ri-information-line text-[#0066ff] mr-2"></i>
                <strong>High-Level Review</strong>
              </p>
              <p className="text-white/70 text-xs md:text-sm leading-relaxed">
                This is a visual and usability review focused on user experience and conversion opportunities. 
                It's not a full technical audit but provides actionable insights to improve your website's performance.
              </p>
            </div>

            <div className="space-y-4 md:space-y-6 mb-8 md:mb-10">
              {[
                'Visual design & user experience assessment',
                'Mobile responsiveness evaluation',
                'Conversion optimization opportunities',
                'Layout and navigation improvements',
                'Clear, actionable recommendations',
              ].map((benefit, index) => (
                <div key={index} className="flex items-start gap-3 md:gap-4">
                  <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center flex-shrink-0 mt-0.5 md:mt-1">
                    <i className="ri-check-line text-[#0066ff] text-xl md:text-2xl"></i>
                  </div>
                  <span className="text-base md:text-lg text-white/80">{benefit}</span>
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-[#8b2bd9]/30 bg-[#8b2bd9]/5 p-5 md:p-6 text-center">
              <p className="text-white/90 text-sm md:text-base">
                <span className="font-semibold">Non-binding review</span> • Delivered in 48 hours • No spam guaranteed
              </p>
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-[#1a1a1a] rounded-[24px] md:rounded-[32px] p-6 md:p-12">
            <div className="mb-6">
              <h3 className="text-xl md:text-2xl font-semibold text-white mb-2">Request Your Audit</h3>
              <p className="text-white/60 text-sm md:text-base">We only need a few details to get started</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
              <input
                type="text"
                name="name"
                placeholder="Your Name *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full h-12 md:h-14 px-4 md:px-5 bg-[#0a0a0a] border border-white/10 rounded-xl md:rounded-2xl text-white text-sm md:text-base placeholder:text-white/40 focus:border-[#0066ff] focus:outline-none focus:shadow-[0_0_20px_rgba(0,102,255,0.2)] transition-all"
              />
              <input
                type="email"
                name="email"
                placeholder="Email Address *"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="w-full h-12 md:h-14 px-4 md:px-5 bg-[#0a0a0a] border border-white/10 rounded-xl md:rounded-2xl text-white text-sm md:text-base placeholder:text-white/40 focus:border-[#0066ff] focus:outline-none focus:shadow-[0_0_20px_rgba(0,102,255,0.2)] transition-all"
              />
              <input
                type="text"
                name="businessName"
                placeholder="Business Name *"
                value={formData.businessName}
                onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                required
                className="w-full h-12 md:h-14 px-4 md:px-5 bg-[#0a0a0a] border border-white/10 rounded-xl md:rounded-2xl text-white text-sm md:text-base placeholder:text-white/40 focus:border-[#0066ff] focus:outline-none focus:shadow-[0_0_20px_rgba(0,102,255,0.2)] transition-all"
              />
              <input
                type="url"
                name="website"
                placeholder="Website URL (optional)"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="w-full h-12 md:h-14 px-4 md:px-5 bg-[#0a0a0a] border border-white/10 rounded-xl md:rounded-2xl text-white text-sm md:text-base placeholder:text-white/40 focus:border-[#0066ff] focus:outline-none focus:shadow-[0_0_20px_rgba(0,102,255,0.2)] transition-all"
              />

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 md:h-16 bg-[#8b2bd9] text-white text-base md:text-lg font-semibold rounded-xl md:rounded-2xl hover:bg-[#7a26c1] hover:translate-y-[-2px] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Submitting...' : 'Request Free Audit'}
              </button>

              {submitStatus === 'success' && (
                <div className="text-center text-[#0066ff] text-sm">
                  ✓ Thank you! Check your email for confirmation.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="text-center text-red-400 text-sm">
                  Something went wrong. Please try again.
                </div>
              )}

              {/* Privacy Notice */}
              <div className="bg-[#0a0a0a]/50 rounded-lg p-3 md:p-4 mt-4">
                <div className="flex items-start gap-2 text-xs md:text-sm text-white/60">
                  <i className="ri-shield-check-line text-green-400 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <p className="mb-1">
                      <strong className="text-white/80">Your data is secure:</strong> Information submitted is stored securely and used only for audit follow-up communication.
                    </p>
                    <p>
                      We'll contact you within 48 hours with your website review and will not share your details with third parties.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center gap-2 text-xs md:text-sm text-white/50 pt-2">
                <i className="ri-lock-line"></i>
                <span>SSL encrypted • GDPR compliant</span>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes gradient-reverse {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-10%, -10%) scale(1.1); }
        }
        .animate-gradient-reverse {
          animation: gradient-reverse 20s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
}
